import React, { useEffect, lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { useLang } from "../i18n";
import { useLenis } from "../hooks/useLenis";
import { Nav } from "../components/Nav";
import { Footer } from "../components/Footer";
import { LeadForm } from "../components/LeadForm";
import { Reveal, SectionLabel, MaskLine } from "../components/Motion";
import { Seo } from "../components/Seo";
import { ArrowLeft, MapPin, Loader2 } from "lucide-react";

const TenuteScene = lazy(() =>
  import("../components/tenute/TenuteScene").then((m) => ({ default: m.TenuteScene }))
);

export default function Tenute() {
  useLenis();
  const { t, lang } = useLang();
  const te = t.tenute;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [lang]);

  return (
    <div className="grain relative">
      <Seo page="tenute" />
      <Nav />
      <main>
        {/* HERO */}
        <section data-testid="tenute-hero" className="relative pt-36 md:pt-44 pb-14 md:pb-20">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <Link to="/" data-testid="tenute-back" className="inline-flex items-center gap-2 font-jetbrains text-[11px] tracking-[0.2em] uppercase text-[var(--text-secondary)] hover:text-[var(--gold)] transition-colors mb-10">
              <ArrowLeft className="w-4 h-4" /> Amazonico3000
            </Link>
            <SectionLabel>{te.label}</SectionLabel>
            <div className="grid lg:grid-cols-12 gap-10 items-end">
              <h1 className="lg:col-span-7 font-cormorant font-light text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-[var(--text-primary)]">
                <MaskLine delay={0.1}>{te.title}</MaskLine>
              </h1>
              <div className="lg:col-span-5">
                <Reveal delay={0.25}>
                  <p className="inline-flex items-center gap-2 font-jetbrains text-[11px] tracking-[0.2em] uppercase text-[var(--gold)]">
                    <MapPin className="w-4 h-4" /> {te.sub.split(".")[0]}.
                  </p>
                  <p className="font-outfit font-light text-[var(--text-secondary)] leading-relaxed mt-4">{te.intro}</p>
                  <Link to="/origano" data-testid="tenute-origano-cta" className="inline-flex items-center gap-2 mt-6 font-jetbrains text-[11px] tracking-[0.2em] uppercase text-[var(--gold)] hover:opacity-80 transition-opacity">
                    {te.origanoCta} →
                  </Link>
                </Reveal>
              </div>
            </div>
          </div>
        </section>

        {/* 3D DRONE EXPERIENCE */}
        <section data-testid="tenute-experience" className="pb-16 md:pb-24">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
              <div className="max-w-2xl">
                <SectionLabel>{te.expLabel}</SectionLabel>
                <h2 className="font-cormorant font-light text-4xl sm:text-5xl leading-[1.05] tracking-tight text-[var(--text-primary)]">{te.expTitle}</h2>
              </div>
              <p className="font-outfit font-light text-[var(--text-secondary)] max-w-md">{te.expSub}</p>
            </div>
            <Suspense
              fallback={
                <div className="w-full h-[72vh] min-h-[480px] rounded-[2rem] border border-[var(--border)] bg-[var(--bg-secondary)] flex items-center justify-center">
                  <Loader2 className="w-8 h-8 animate-spin text-[var(--gold)]" />
                </div>
              }
            >
              <TenuteScene t={te} />
            </Suspense>
          </div>
        </section>

        {/* FACTS */}
        <section data-testid="tenute-facts" className="py-16 md:py-24 bg-[var(--bg-secondary)] border-y border-[var(--border)]">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <SectionLabel>{te.factsLabel}</SectionLabel>
            <h2 className="font-cormorant font-light text-4xl sm:text-5xl leading-[1.05] tracking-tight text-[var(--text-primary)] mb-14">{te.factsTitle}</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {te.facts.map((f, i) => (
                <Reveal key={i} delay={i * 0.1}>
                  <div data-testid={`tenute-fact-${i + 1}`} className="h-full rounded-2xl border border-[var(--border)] bg-[var(--bg-primary)] p-8">
                    <span className="font-jetbrains text-[11px] tracking-[0.2em] text-[var(--gold)]">{f.n}</span>
                    <h3 className="font-cormorant text-2xl md:text-3xl text-[var(--text-primary)] mt-3">{f.t}</h3>
                    <p className="font-outfit font-light text-sm text-[var(--text-secondary)] leading-relaxed mt-3">{f.d}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <LeadForm source="tenute" interests={te.interests} label={te.label} />
      </main>
      <Footer />
    </div>
  );
}
